import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["imageSrc", "name", "description", "disableTooltip", "contentType", "lengthType", "length", "progress", "awardLink", "bordered", "linkMode", "contentHref", "cardMenu", "onClick", "ribbonText", "ribbonUse", "thumbnailOverlay", "use", "size", "footer", "isGhost", "data-test-id", "menuAlwaysVisible", "isInternalContent", "selected", "cardBody", "onThumbnailLoad", "cardRef", "underlineOnHover"];
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { CardHeader, CardSection, CardWrapper } from 'foundations-components/transitional/components/Card';
import { Image } from 'foundations-components/transitional/components/Image';
import { Ribbon } from 'foundations-components/transitional/components/Ribbon';
import { useState } from 'react';
import { PolymorphicLink } from '../PolymorphicLink';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import FormattedDuration from 'I18n/components/FormattedDuration';
import { SuccessIcon } from 'foundations-assets/icons/components/SuccessIcon';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import { StatusDot } from 'foundations-components/transitional/components/StatusDot';
import { ContentTypeBadgeMap, ClampedText } from './shared';
import { stringToHash } from '../../utils/math';
import ACDynamicThumbnailImage from './ACDynamicThumbnailImage';
import { View } from 'foundations-components/components/View';
import { Box } from 'foundations-components/transitional/components/Box';
import { useMediaQuery } from '../../utils/Styling';
import { NanoProgress } from 'foundations-components/transitional/components/NanoProgress';
import { useTheme } from 'foundations-theming/utils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
export function ACContentCardSmall(_ref) {
  let {
      imageSrc,
      name,
      description,
      disableTooltip = false,
      contentType,
      lengthType,
      length,
      progress,
      awardLink,
      bordered = true,
      linkMode = 'default',
      contentHref,
      cardMenu,
      onClick,
      ribbonText,
      ribbonUse,
      thumbnailOverlay,
      use: useProp = 'default',
      size = 60,
      footer,
      isGhost,
      'data-test-id': dataTestId = 'academy-content-card-small',
      menuAlwaysVisible,
      isInternalContent = false,
      selected,
      cardBody,
      onThumbnailLoad,
      cardRef,
      underlineOnHover = true
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  const overviewType = props.overviewType;
  const use = bordered || ribbonText ? 'default' : useProp;
  const [isHovering, setIsHovering] = useState(false);
  const {
    isTouchscreen
  } = useMediaQuery();
  const theme = useTheme();
  const ribbon = ribbonText || isInternalContent ? /*#__PURE__*/_jsx(Ribbon, {
    position: "right",
    use: isInternalContent ? 'norman' : ribbonUse,
    children: isInternalContent ? /*#__PURE__*/_jsx(FormattedMessage, {
      message: "academyCommon.contentCard.badge.internal"
    }) : ribbonText
  }) : null;
  const content = /*#__PURE__*/_jsxs(Flex, Object.assign({
    alignItems: "center",
    "data-test-id": dataTestId,
    inlineSize: "100%",
    onClick: onClick,
    onMouseEnter: () => setIsHovering(true),
    onMouseLeave: () => setIsHovering(false),
    position: "relative",
    zIndex: "0"
  }, props, {
    children: [/*#__PURE__*/_jsx(Box, {
      grow: 1,
      children: /*#__PURE__*/_jsx("div", {
        style: {
          opacity: isGhost ? 0.5 : 1
        },
        children: /*#__PURE__*/_jsx(PolymorphicLink, {
          href: contentHref,
          linkMode: linkMode,
          use: "unstyled",
          children: /*#__PURE__*/_jsxs(Flex, {
            alignItems: "center",
            gap: "400",
            children: [/*#__PURE__*/_jsxs(View, {
              minBlockSize: `${size}px`,
              minInlineSize: `${size}px`,
              position: "relative",
              children: [imageSrc ? /*#__PURE__*/_jsx(Image, {
                alt: name,
                blockSize: `${size}px`,
                borderRadius: "200",
                inlineSize: `${size}px`,
                loading: 'lazy',
                objectFit: "cover",
                onLoad: onThumbnailLoad,
                src: imageSrc
              }) : /*#__PURE__*/_jsx(View, {
                maxBlockSize: `${size}px`,
                maxInlineSize: `${size}px`,
                minBlockSize: `${size}px`,
                minInlineSize: `${size}px`,
                children: /*#__PURE__*/_jsx(ACDynamicThumbnailImage, {
                  borderRadius: "200",
                  seed: stringToHash(name + contentType + contentHref),
                  title: name,
                  type: "square"
                })
              }), /*#__PURE__*/_jsx(View, {
                blockSize: "6px",
                inlineSize: `${size}px`,
                insetBlockEnd: "0",
                insetInlineStart: "0",
                position: "absolute",
                children: /*#__PURE__*/_jsx(NanoProgress, {
                  color: theme.color['fill-accent-teal-default'],
                  height: 6,
                  value: (progress !== null && progress !== void 0 ? progress : 0) / length * 100
                })
              }), thumbnailOverlay && /*#__PURE__*/_jsx(View, {
                inset: "0",
                position: "absolute",
                children: thumbnailOverlay
              })]
            }), /*#__PURE__*/_jsxs(Flex, {
              flexDirection: "column",
              flexGrow: 1,
              gap: "200",
              justifyContent: "center",
              minBlockSize: `${size}px`,
              children: [/*#__PURE__*/_jsx(Tooltip, {
                delay: 500,
                open: disableTooltip ? false : undefined,
                title: name,
                children: /*#__PURE__*/_jsx(Text, {
                  as: "h6",
                  use: use,
                  children: /*#__PURE__*/_jsx(ClampedText, {
                    underlineOnHover: underlineOnHover,
                    children: name
                  })
                })
              }), description && /*#__PURE__*/_jsx(Text, {
                children: /*#__PURE__*/_jsx(ClampedText, {
                  children: description
                })
              }), footer || /*#__PURE__*/_jsx(Text, {
                as: "small",
                marginBlockStart: "50",
                use: use === 'default' ? 'help' : 'on-dark',
                children: !progress ? /*#__PURE__*/_jsxs(List, {
                  paddingInlineStart: "0 !important",
                  use: "inline-divided",
                  children: [contentType && /*#__PURE__*/_jsx(ListItem, {
                    children: ContentTypeBadgeMap[contentType]
                  }), !!length && /*#__PURE__*/_jsx(ListItem, {
                    children: lengthType === 'items' ? /*#__PURE__*/_jsx(FormattedMessage, {
                      message: "academyCommon.contentCard.itemCount",
                      options: {
                        count: length
                      }
                    }) : /*#__PURE__*/_jsx(FormattedDuration
                    // eslint-disable-next-line react-hooks/purity
                    , {
                      from: Date.now() - length * 1000
                    })
                  })]
                }) : progress === length ? /*#__PURE__*/_jsxs(Flex, {
                  flexWrap: "wrap",
                  gap: "200",
                  justifyContent: "space-between",
                  children: [/*#__PURE__*/_jsxs(Flex, {
                    alignItems: "center",
                    gap: "100",
                    children: [/*#__PURE__*/_jsx(SuccessIcon, {
                      color: "fill-positive-default"
                    }), /*#__PURE__*/_jsx(Text, {
                      as: "small",
                      children: ['VIDEO', 'ACADEMY_VIDEO'].includes(contentType !== null && contentType !== void 0 ? contentType : '') ? /*#__PURE__*/_jsx(FormattedMessage, {
                        message: "academyCommon.progress.watched"
                      }) : /*#__PURE__*/_jsx(FormattedMessage, {
                        message: "academyCommon.progress.completed"
                      })
                    })]
                  }), awardLink]
                }) : /*#__PURE__*/_jsxs(List, {
                  paddingInlineStart: "0 !important",
                  use: "inline-divided",
                  children: [contentType && /*#__PURE__*/_jsx(ListItem, {
                    children: ContentTypeBadgeMap[contentType]
                  }), !!length && /*#__PURE__*/_jsxs(ListItem, {
                    children: [/*#__PURE__*/_jsx(StatusDot, {
                      use: "active"
                    }), lengthType === 'items' ? /*#__PURE__*/_jsx(FormattedMessage, {
                      message: "academyCommon.contentCard.itemsRemaining",
                      options: {
                        count: length - progress
                      }
                    }) : /*#__PURE__*/_jsx(FormattedReactMessage, {
                      message: "academyCommon.tracks.timeRemaining.duration",
                      options: {
                        duration: /*#__PURE__*/_jsx(FormattedDuration, {
                          from:
                          // eslint-disable-next-line react-hooks/purity
                          Date.now() - (length - progress) * 1000
                        })
                      }
                    })]
                  })]
                })
              })]
            })]
          })
        })
      })
    }), cardMenu && /*#__PURE__*/_jsx(Flex, {
      alignItems: "center",
      blockSize: "100%",
      marginInline: "400",
      visibility: menuAlwaysVisible || isHovering || isTouchscreen ? 'visible' : 'hidden',
      children: cardMenu
    }), !cardMenu && bordered && ribbon && /*#__PURE__*/_jsx(Box, {
      minInlineSize: "70px"
    }), ' ', !bordered && ribbon && /*#__PURE__*/_jsx(Box, {
      minInlineSize: "100px",
      children: ribbon
    })]
  }));
  const cardWrapperBody = overviewType === 'course-overview-curriculum' ? /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(CardHeader, {
      paddingInlineEnd: "200",
      title: content
    }), /*#__PURE__*/_jsx(CardSection, {
      paddingInlineEnd: "500",
      children: cardBody
    })]
  }) : /*#__PURE__*/_jsx(CardSection, {
    paddingInlineEnd: "200",
    children: content
  });
  return bordered ? /*#__PURE__*/_jsxs(CardWrapper, {
    borderColor: selected ? 'border-interactive-pressed' : undefined,
    borderStyle: selected ? 'solid' : undefined,
    borderWidth: selected ? '200' : undefined,
    compact: true,
    defaultExpanded: false,
    expandable: overviewType === 'course-overview-curriculum',
    inlineSize: "100%",
    marginBlock: overviewType === 'course-overview-curriculum' ? '500' : '0',
    marginInline: "0",
    position: "relative",
    ref: cardRef,
    zIndex: "0",
    children: [ribbon && /*#__PURE__*/_jsx(View, {
      insetBlockStart: "0",
      insetInlineEnd: "0",
      position: "absolute",
      zIndex: "11",
      children: ribbon
    }), cardWrapperBody]
  }) : cardRef ? /*#__PURE__*/_jsx("div", {
    ref: cardRef,
    children: content
  }) : content;
}